import React, { useEffect, useState } from "react";
import { Footprints, Loader2, Check, X, MapPin, User, ChevronDown, ChevronUp, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "../../lib/api";

/**
 * iter109 — Admin moderation queue for crowdsourced walks.
 * iter156 — Condensed: collapsed by default with a summary row, plus a
 * one-click "Approve all pending" button so the operator doesn't have
 * to scroll through hundreds of rows to catch up. Tap the header to
 * expand the full list if per-row review is needed.
 */
export default function WalksReviewCard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/walks/pending`);
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const approve = async (walkId) => {
    setBusyId(walkId);
    try {
      await api.post(`/admin/walks/${walkId}/approve`);
      toast.success("Walk published");
      setItems((prev) => prev.filter((w) => w.walk_id !== walkId));
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (walkId) => {
    if (!window.confirm("Reject this walk submission?")) return;
    setBusyId(walkId);
    try {
      await api.post(`/admin/walks/${walkId}/reject`);
      toast.success("Walk rejected");
      setItems((prev) => prev.filter((w) => w.walk_id !== walkId));
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusyId(null);
    }
  };

  const approveAll = async () => {
    if (!items.length) return;
    if (!window.confirm(`Approve ALL ${items.length} pending walks in one go? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/admin/walks/approve-all`);
      toast.success(`Approved ${data.approved} walk${data.approved === 1 ? "" : "s"} — inbox cleared.`);
      setItems([]);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="mb-6 p-5 border hairline rounded-2xl bg-white" data-testid="walks-review-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-2 text-left min-w-0"
          data-testid="walks-review-toggle"
        >
          <Footprints className="w-4 h-4 text-[#0a7f3f] shrink-0" />
          <div>
            <div className="font-display text-base sm:text-lg tracking-tight">
              Walks review queue
            </div>
            <div className="text-xs text-[#666]">
              {loading ? "Loading…" : `${items.length} pending`}
              {items.length > 0 && <> · tap to {expanded ? "collapse" : "expand"}</>}
            </div>
          </div>
          {items.length > 0 && (expanded
            ? <ChevronUp className="w-4 h-4 text-[#666]" />
            : <ChevronDown className="w-4 h-4 text-[#666]" />)}
        </button>

        {items.length > 0 && (
          <button
            type="button"
            onClick={approveAll}
            disabled={bulkBusy}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#0a7f3f] text-white text-xs font-black uppercase tracking-wider hover:bg-[#046030] disabled:opacity-60"
            data-testid="walks-review-approve-all"
          >
            {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
            Approve all {items.length}
          </button>
        )}
      </div>

      {!loading && items.length === 0 && (
        <div className="text-xs text-[#888] mt-3">
          Inbox zero — no pending walk submissions.
        </div>
      )}

      {expanded && items.length > 0 && (
        <div className="space-y-3 mt-4">
          {items.map((w) => (
            <div key={w.walk_id} className="border hairline rounded-lg bg-white p-4 grid grid-cols-1 md:grid-cols-[120px_1fr_auto] gap-3 items-start" data-testid={`walks-review-row-${w.walk_id}`}>
              {(w.photos && w.photos[0]?.image_data_url) ? (
                <img src={w.photos[0].image_data_url} alt={w.name} className="w-full md:w-[120px] h-20 object-cover rounded" />
              ) : (
                <div className="w-full md:w-[120px] h-20 bg-[#f5f5f5] flex items-center justify-center rounded">
                  <MapPin className="w-5 h-5 text-[#888]" />
                </div>
              )}
              <div className="min-w-0">
                <div className="font-display text-base leading-tight" data-testid={`walks-review-name-${w.walk_id}`}>{w.name}</div>
                <div className="text-[11px] text-[#666] mt-0.5 flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1"><User className="w-3 h-3" /> {w.submitted_by_name || "Anonymous"}</span>
                  <span>·</span>
                  <span>{w.campsite_id}</span>
                  {w.length_km && <><span>·</span><span>{w.length_km} km</span></>}
                  {w.difficulty && <><span>·</span><span>{w.difficulty}</span></>}
                </div>
                <p className="text-[12px] text-[#333] leading-snug mt-2">{w.description}</p>
              </div>
              <div className="flex md:flex-col gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => approve(w.walk_id)}
                  disabled={busyId === w.walk_id}
                  className="bg-[#0a7f3f] text-white text-[11px] uppercase tracking-wider font-bold py-2 px-3 inline-flex items-center gap-1 rounded disabled:opacity-50"
                  data-testid={`walks-review-approve-${w.walk_id}`}
                >
                  {busyId === w.walk_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Approve
                </button>
                <button
                  type="button"
                  onClick={() => reject(w.walk_id)}
                  disabled={busyId === w.walk_id}
                  className="border hairline text-[#b00020] text-[11px] uppercase tracking-wider font-bold py-2 px-3 inline-flex items-center gap-1 hover:bg-[#fff0f0] rounded disabled:opacity-50"
                  data-testid={`walks-review-reject-${w.walk_id}`}
                >
                  <X className="w-3 h-3" /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
